
import { useState } from 'react';
import Card from '../../../components/base/Card';
import Button from '../../../components/base/Button';

export default function BenefitsSection() {
  const [activeTab, setActiveTab] = useState('daily');
  const [subscribedEmail, setSubscribedEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscribeMessage, setSubscribeMessage] = useState('');

  const benefits = {
    daily: [
      {
        id: 1,
        title: '每日签到奖励',
        description: '连续签到获得积分，积分可兑换精美礼品',
        icon: 'ri-calendar-check-line',
        value: '10-100积分',
        color: 'from-blue-500 to-cyan-500'
      },
      {
        id: 2,
        title: '每日免费抽奖',
        description: '每天一次免费抽奖机会，100%中奖',
        icon: 'ri-gift-line',
        value: '必中奖励',
        color: 'from-green-500 to-emerald-500'
      },
      {
        id: 3,
        title: '每日任务奖励',
        description: '完成简单任务，获得额外福利',
        icon: 'ri-task-line',
        value: '丰厚奖励',
        color: 'from-purple-500 to-violet-500'
      }
    ],
    weekly: [
      {
        id: 4,
        title: '周末特别福利',
        description: '周末专享双倍奖励和特殊活动',
        icon: 'ri-weekend-line',
        value: '双倍奖励',
        color: 'from-orange-500 to-red-500'
      },
      {
        id: 5,
        title: '周排行榜奖励',
        description: '活跃度排行榜前100名获得专属奖励',
        icon: 'ri-trophy-line',
        value: '排行奖励',
        color: 'from-yellow-500 to-orange-500'
      },
      {
        id: 6,
        title: '周任务挑战',
        description: '完成周任务挑战，解锁限定奖励',
        icon: 'ri-flag-line',
        value: '限定奖励',
        color: 'from-pink-500 to-rose-500'
      }
    ],
    special: [
      {
        id: 7,
        title: '生日专属福利',
        description: '生日当天享受专属生日礼包',
        icon: 'ri-cake-3-line',
        value: '生日礼包',
        color: 'from-indigo-500 to-purple-500'
      },
      {
        id: 8,
        title: 'VIP会员特权',
        description: '升级VIP享受更多专属福利和优先权',
        icon: 'ri-vip-crown-line',
        value: 'VIP特权',
        color: 'from-amber-500 to-yellow-500'
      },
      {
        id: 9,
        title: '节日限定活动',
        description: '重要节日推出限时特别活动和奖励',
        icon: 'ri-fire-line',
        value: '节日大礼',
        color: 'from-red-500 to-pink-500'
      }
    ]
  };

  const tabs = [
    { id: 'daily', name: '每日福利', icon: 'ri-sun-line' },
    { id: 'weekly', name: '每周福利', icon: 'ri-calendar-week-line' },
    { id: 'special', name: '特殊福利', icon: 'ri-star-line' }
  ];

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subscribedEmail.trim()) return;

    setIsSubscribing(true);
    try {
      const formData = new FormData();
      formData.append('email', subscribedEmail);
      formData.append('timestamp', new Date().toISOString());

      const response = await fetch('https://readdy.ai/api/form/d4478gmpguuav66rgbag', {
        method: 'POST',
        body: new URLSearchParams(formData as any)
      });

      if (response.ok) {
        setSubscribeMessage('订阅成功！我们会第一时间通知您最新福利信息');
        setSubscribedEmail('');
      } else {
        setSubscribeMessage('订阅失败，请稍后重试');
      }
    } catch (error) {
      setSubscribeMessage('订阅失败，请稍后重试');
    } finally {
      setIsSubscribing(false);
      setTimeout(() => setSubscribeMessage(''), 3000);
    }
  };

  return (
    <section id="benefits" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
            <strong>福利中心</strong>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            丰富多样的福利等你来领取，从每日签到到特殊节日，我们为你准备了满满的惊喜和奖励！
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center mb-12">
          <div className="bg-gray-100 rounded-full p-1 inline-flex">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-200 flex items-center space-x-2 cursor-pointer ${
                  activeTab === tab.id
                    ? 'bg-white text-purple-600 shadow-md'
                    : 'text-gray-600 hover:text-purple-600'
                }`}
              >
                <i className={`${tab.icon} text-lg`}></i>
                <span>{tab.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {benefits[activeTab as keyof typeof benefits].map((benefit) => (
            <Card key={benefit.id} hover className="text-center">
              <div className={`w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r ${benefit.color} flex items-center justify-center`}>
                <i className={`${benefit.icon} text-2xl text-white`}></i>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">{benefit.title}</h3>
              <p className="text-gray-600 mb-4 leading-relaxed">{benefit.description}</p>
              <div className={`inline-block px-4 py-2 rounded-full bg-gradient-to-r ${benefit.color} text-white font-medium text-sm`}>
                {benefit.value}
              </div>
            </Card>
          ))}
        </div>

        {/* Newsletter Subscription */}
        <div className="max-w-4xl mx-auto">
          <Card gradient className="text-center">
            <div className="mb-6">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center">
                <i className="ri-mail-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-3">订阅福利通知</h3>
              <p className="text-gray-600 leading-relaxed">
                订阅我们的福利通知，第一时间获取最新活动信息和专属优惠，不错过任何惊喜！
              </p>
            </div>

            <form onSubmit={handleSubscribe} data-readdy-form className="max-w-md mx-auto">
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  value={subscribedEmail}
                  onChange={(e) => setSubscribedEmail(e.target.value)}
                  placeholder="请输入您的邮箱地址"
                  className="flex-1 px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  name="email"
                  required
                />
                <Button 
                  type="submit" 
                  disabled={isSubscribing || !subscribedEmail.trim()}
                  className="whitespace-nowrap"
                >
                  {isSubscribing ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      订阅中...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2"></i>
                      立即订阅
                    </>
                  )}
                </Button>
              </div>
              {subscribeMessage && (
                <div className={`mt-4 p-3 rounded-lg text-center ${
                  subscribeMessage.includes('成功') 
                    ? 'bg-green-100 text-green-800 border border-green-200' 
                    : 'bg-red-100 text-red-800 border border-red-200'
                }`}>
                  {subscribeMessage}
                </div>
              )}
            </form>
          </Card>
        </div>
      </div>
    </section>
  );
}
